import { createSlice } from '@reduxjs/toolkit';
import { getMktOverview, getMktInstagramInsights, getMktFacebookInsights, getMktGoogleAdsInsights } from 'src/actions/v2/marketing/analytics/v2_actionMktInsights';

// O backend responde 409/401 quando o token da Meta perdeu (ou nunca teve) a
// permissao de insights. Guardar so o objeto Error do axios deixava o motivo
// inacessivel pro componente — e nao e serializavel pelo redux.
function normalizeError(error) {
    // Os actions daqui usam o axios global (rejeita com AxiosError), mas
    // src/utils/axios.js rejeita ja com o corpo — aceita as duas formas.
    const body = error?.response?.data || (error?.kind ? error : null);
    if (body?.kind) {
        return {
            kind: body.kind,
            platform: body.platform || null,
            message: body.message || null,
            missing_scopes: body.missing_scopes || [],
            graph_message: body.graph_message || null
        };
    }
    return {
        kind: 'UPSTREAM_ERROR',
        platform: null,
        message: body?.message || error?.message || 'Não foi possível carregar os insights.',
        missing_scopes: [],
        graph_message: null
    };
}

const initialState = {
    isLoading: false,
    error: null,
    insights: {
        overview: null,
        instagram: null,
        facebook: null,
        googleAds: null
    },
    activeTab: 'overview'
};

const slice = createSlice({
    name: 'mktAnalytics',
    initialState,
    reducers: {
        startLoading(state) {
            state.isLoading = true;
            // Sem isto o erro de uma aba persiste ao trocar para outra.
            state.error = null;
        },
        hasError(state, action) {
            state.isLoading = false;
            state.error = action.payload;
        },
        getOverviewSuccess(state, action) {
            state.isLoading = false;
            state.insights.overview = action.payload;
            // O overview responde 200 mesmo com uma plataforma quebrada; o
            // motivo vem em data.warnings.
            state.error = action.payload?.warnings?.[0] || null;
        },
        getInstagramSuccess(state, action) {
            state.isLoading = false;
            state.insights.instagram = action.payload;
        },
        getFacebookSuccess(state, action) {
            state.isLoading = false;
            state.insights.facebook = action.payload;
        },
        getGoogleAdsSuccess(state, action) {
            state.isLoading = false;
            state.insights.googleAds = action.payload;
        },
        setActiveTab(state, action) {
            state.activeTab = action.payload;
        }
    }
});

export default slice.reducer;
export const { setActiveTab } = slice.actions;

export function fetchOverview(clienteId, querys) {
    return async (dispatch) => {
        dispatch(slice.actions.startLoading());
        try {
            const response = await getMktOverview(clienteId, querys);
            dispatch(slice.actions.getOverviewSuccess(response.data.data));
        } catch (error) {
            dispatch(slice.actions.hasError(normalizeError(error)));
        }
    };
}

export function fetchInstagramInsights(clienteId, querys) {
    return async (dispatch) => {
        dispatch(slice.actions.startLoading());
        try {
            const response = await getMktInstagramInsights(clienteId, querys);
            dispatch(slice.actions.getInstagramSuccess(response.data.data));
        } catch (error) {
            dispatch(slice.actions.hasError(normalizeError(error)));
        }
    };
}

export function fetchFacebookInsights(clienteId, querys) {
    return async (dispatch) => {
        dispatch(slice.actions.startLoading());
        try {
            const response = await getMktFacebookInsights(clienteId, querys);
            dispatch(slice.actions.getFacebookSuccess(response.data.data));
        } catch (error) {
            dispatch(slice.actions.hasError(normalizeError(error)));
        }
    };
}

export function fetchGoogleAdsInsights(clienteId, querys) {
    return async (dispatch) => {
        dispatch(slice.actions.startLoading());
        try {
            const response = await getMktGoogleAdsInsights(clienteId, querys);
            dispatch(slice.actions.getGoogleAdsSuccess(response.data.data));
        } catch (error) {
            dispatch(slice.actions.hasError(normalizeError(error)));
        }
    };
}
